/**
 * Os formularios de acesso sem a moldura da pagina: entrar e "esqueci minha senha". As telas de acesso (`telas/Acesso`)
 * e o checkout (spec 045, comprar logado) usam os mesmos, pra que entrar tenha a mesma cara e o mesmo comportamento
 * onde quer que apareca. So apresentacao: recebem estado e devolvem acoes.
 */
import { useState, type FormEvent } from "react";
import { cpfValido, emailValido, formatarTelefone, soDigitos, sugerirDominioDeEmail } from "../formatos";
import { LinkInterno } from "../navegacao";
import { Aviso } from "./Basicos";
import { Botao } from "./Botao";
import { Campo, CampoCodigo, CampoCpf, CampoCpfOuEmail, CampoSenha } from "./Campo";

export const ROTAS_SUPORTE = { email: "/suporte/email", senha: "/suporte/senha", cpf: "/suporte/cpf" } as const;
export const SENHA_MIN = 8;

const nota = { margin: 0, fontSize: "var(--t-sm)" } as const;

// ---------------------------------------------------------------- entrar (CPF ou e-mail e senha)

export function FormularioEntrar({ erro, aviso, carregando, identificadorInicial = "", autoFocus = true, aoEnviar }: {
  erro?: string;
  aviso?: string;
  carregando?: boolean;
  /** CPF ou e-mail ja conhecido (ex.: o checkout soube que o CPF ja tem conta). */
  identificadorInicial?: string;
  autoFocus?: boolean;
  aoEnviar: (d: { identificador: string; senha: string }) => void;
}) {
  const [identificador, setIdentificador] = useState(identificadorInicial);
  const [senha, setSenha] = useState("");
  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (identificador.trim() && senha) aoEnviar({ identificador: identificador.trim(), senha });
  };
  return (
    <form className="mtc-acesso__campos" onSubmit={enviar} noValidate>
      {aviso && <Aviso tipo="sucesso">{aviso}</Aviso>}
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <CampoCpfOuEmail valor={identificador} aoMudar={setIdentificador} autoFocus={autoFocus && !identificadorInicial} required />
      <CampoSenha valor={senha} aoMudar={setSenha} autoFocus={autoFocus && Boolean(identificadorInicial)} required />
      <Botao type="submit" largo carregando={carregando} disabled={!identificador.trim() || !senha}>Entrar</Botao>
    </form>
  );
}

// ---------------------------------------------------------------- esqueci minha senha (cliente e administrador)

/**
 * Os dois passos: o CPF ou e-mail (o codigo vai pro e-mail principal) e depois o codigo com a senha nova. O erro fica
 * com quem usa (a tela mostra acima das etapas; o checkout, acima do formulario).
 */
export function FormularioEsqueciSenha({ publico, etapa, emailMascarado, carregando, identificadorInicial = "", aoPedirCodigo, aoConfirmar, aoTrocarDado }: {
  publico: "area" | "painel";
  etapa: "identificar" | "codigo";
  emailMascarado?: string;
  carregando?: boolean;
  identificadorInicial?: string;
  aoPedirCodigo: (identificador: string) => void;
  aoConfirmar: (d: { codigo: string; novaSenha: string }) => void;
  aoTrocarDado?: () => void;
}) {
  const [identificador, setIdentificador] = useState(identificadorInicial);
  const [codigo, setCodigo] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const area = publico === "area";
  const senhaCurta = novaSenha.length > 0 && novaSenha.length < SENHA_MIN;
  return etapa === "identificar" ? (
    <form className="mtc-acesso__campos" onSubmit={(e) => { e.preventDefault(); if (identificador.trim()) aoPedirCodigo(identificador.trim()); }} noValidate>
      {area ? <CampoCpfOuEmail valor={identificador} aoMudar={setIdentificador} autoFocus /> : <Campo rotulo="E-mail" type="email" valor={identificador} aoMudar={setIdentificador} autoComplete="username" autoFocus />}
      <Botao type="submit" largo carregando={carregando} disabled={!identificador.trim()}>Enviar código</Botao>
      {area && <p className="mtc-texto-2" style={nota}>Nunca definiu uma senha? Este mesmo caminho serve: informe seu CPF e siga os passos.</p>}
    </form>
  ) : (
    <form className="mtc-acesso__campos" onSubmit={(e) => { e.preventDefault(); if (codigo.length === 6 && novaSenha.length >= SENHA_MIN) aoConfirmar({ codigo, novaSenha }); }} noValidate>
      <Aviso tipo="info">
        {emailMascarado ? <>Enviamos um código para <strong className="mtc-mono">{emailMascarado}</strong>. Ele vale por 10 minutos.</> : <>Se o dado estiver cadastrado, enviamos um código para o e-mail principal. Ele vale por 10 minutos.</>}
      </Aviso>
      <CampoCodigo valor={codigo} aoMudar={setCodigo} autoFocus />
      <CampoSenha rotulo="Nova senha" valor={novaSenha} aoMudar={setNovaSenha} autoComplete="new-password" dica={`Mínimo de ${SENHA_MIN} caracteres.`} erro={senhaCurta ? `A senha precisa de pelo menos ${SENHA_MIN} caracteres.` : undefined} />
      <Botao type="submit" largo carregando={carregando} disabled={codigo.length !== 6 || novaSenha.length < SENHA_MIN}>{area ? "Salvar e entrar" : "Salvar nova senha"}</Botao>
      <div className="mtc-acesso__linha">
        {aoTrocarDado && <button type="button" className="mtc-botao mtc-botao--fantasma mtc-botao--pequeno" onClick={aoTrocarDado}>Pedir outro código</button>}
        {area && <LinkInterno href={ROTAS_SUPORTE.email}>O e-mail está errado?</LinkInterno>}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------- criar a conta (spec 045)

/** Aviso de dominio digitado errado ("gmai.com"): diz o provavel e oferece usar. Quem decide e quem digita. */
export function SugestaoDeDominio({ sugestao, aoUsar }: { sugestao: string; aoUsar: () => void }) {
  return (
    <Aviso tipo="alerta">
      <span className="mtc-sugestao-dominio">
        <span>O domínio parece digitado errado. Você quis dizer <strong className="mtc-mono" style={{ overflowWrap: "anywhere" }}>{sugestao}</strong>?</span>
        {/* O <wbr> quebra a linha antes do "@" no celular; o nome acessivel continua o e-mail inteiro. */}
        <Botao tamanho="pequeno" variante="secundario" onClick={aoUsar} aria-label={`Usar ${sugestao}`}>Usar {sugestao.includes("@") ? <>{sugestao.slice(0, sugestao.indexOf("@"))}<wbr />{sugestao.slice(sugestao.indexOf("@"))}</> : sugestao}</Botao>
      </span>
    </Aviso>
  );
}

export type DadosDoCadastro = { nome: string; cpf: string; email: string; telefone: string };
export const CADASTRO_VAZIO: DadosDoCadastro = { nome: "", cpf: "", email: "", telefone: "" };
/** O que vai pro servidor: CPF e celular so com os digitos, e-mail sem espaco e em minusculas. */
export const cadastroLimpo = (d: DadosDoCadastro): DadosDoCadastro => ({ nome: d.nome.trim(), cpf: soDigitos(d.cpf), email: d.email.trim().toLowerCase(), telefone: soDigitos(d.telefone) });

/**
 * Nome, CPF, e-mail e celular de quem cria a conta. Os dados ficam com quem usa (`valor`), pra que voltar do codigo pra
 * corrigir nao apague nada. A conferencia aqui e so ajuda de digitacao: quem decide e o servidor.
 */
export function FormularioCadastro({ valor, aoMudar, carregando, focarEmail, aoEnviar }: {
  valor: DadosDoCadastro;
  aoMudar: (d: DadosDoCadastro) => void;
  carregando?: boolean;
  /** Abre com o cursor no e-mail (voltou porque o e-mail ja era de outra conta). */
  focarEmail?: boolean;
  aoEnviar: (d: DadosDoCadastro) => void;
}) {
  const [tentou, setTentou] = useState(false);
  const d = valor;
  const mudar = (parte: Partial<DadosDoCadastro>) => aoMudar({ ...d, ...parte });
  const email = d.email.trim().toLowerCase();
  const sugestao = emailValido(email) ? sugerirDominioDeEmail(email) : null;
  const erros = {
    nome: d.nome.trim().length < 3 ? "Escreva o nome completo." : undefined,
    cpf: !cpfValido(d.cpf) ? "Confira o CPF." : undefined,
    email: !emailValido(email) ? "Confira o e-mail." : undefined,
    telefone: soDigitos(d.telefone).length < 10 ? "Informe o celular com DDD." : undefined,
  };
  const enviar = (e: FormEvent) => {
    e.preventDefault();
    setTentou(true);
    if (Object.values(erros).some(Boolean)) return;
    aoEnviar(cadastroLimpo(d));
  };
  return (
    <form className="mtc-acesso__campos" onSubmit={enviar} noValidate>
      <Campo rotulo="Nome completo" valor={d.nome} aoMudar={(nome) => mudar({ nome })} autoComplete="name" placeholder="Ex.: Ana Silva" erro={tentou ? erros.nome : undefined} autoFocus={!focarEmail} required />
      <CampoCpf valor={d.cpf} aoMudar={(cpf) => mudar({ cpf })} dica="Sua conta e as suas compras ficam neste CPF." erro={tentou ? erros.cpf : undefined} required />
      <Campo rotulo="E-mail" type="email" inputMode="email" valor={d.email} aoMudar={(v) => mudar({ email: v.replace(/\s/g, "") })} autoComplete="email" autoCapitalize="none" spellCheck={false} placeholder="Ex.: ana@email.com" dica="Enviamos um código pra confirmar que o e-mail é seu." erro={tentou ? erros.email : undefined} autoFocus={focarEmail} required />
      {sugestao && <SugestaoDeDominio sugestao={sugestao} aoUsar={() => mudar({ email: sugestao })} />}
      <Campo rotulo="Celular com DDD" valor={d.telefone} aoMudar={(v) => mudar({ telefone: formatarTelefone(v) })} inputMode="tel" autoComplete="tel" placeholder="(11) 98765-4321" erro={tentou ? erros.telefone : undefined} required />
      <Botao type="submit" largo carregando={carregando}>Enviar código</Botao>
    </form>
  );
}

/** O codigo que chegou no e-mail e a senha da conta nova. So com o codigo certo a conta nasce (a tela nao decide). */
export function FormularioCodigoDoCadastro({ emailMascarado, carregando, aoConfirmar }: { emailMascarado: string; carregando?: boolean; aoConfirmar: (d: { codigo: string; senha: string }) => void }) {
  const [codigo, setCodigo] = useState("");
  const [senha, setSenha] = useState("");
  const senhaCurta = senha.length > 0 && senha.length < SENHA_MIN;
  return (
    <form className="mtc-acesso__campos" onSubmit={(e) => { e.preventDefault(); if (codigo.length === 6 && senha.length >= SENHA_MIN) aoConfirmar({ codigo, senha }); }} noValidate>
      <Aviso tipo="info">Enviamos um código de 6 dígitos para <strong className="mtc-mono" style={{ overflowWrap: "anywhere" }}>{emailMascarado}</strong>. Não achou? Olhe o spam e as promoções.</Aviso>
      <CampoCodigo valor={codigo} aoMudar={setCodigo} autoFocus />
      <CampoSenha rotulo="Crie uma senha" valor={senha} aoMudar={setSenha} autoComplete="new-password" dica={`Mínimo de ${SENHA_MIN} caracteres. É com ela que você entra na sua área.`} erro={senhaCurta ? `A senha precisa de pelo menos ${SENHA_MIN} caracteres.` : undefined} />
      <Botao type="submit" largo carregando={carregando} disabled={codigo.length !== 6 || senha.length < SENHA_MIN}>Criar conta e continuar</Botao>
    </form>
  );
}
